/**
 * QuestAnalyticsTab — Chart.js powered analytics for quests
 * XP banner + completion funnel + quest-specific charts
 */

import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut, PolarArea } from 'react-chartjs-2';
import { Box, CircularProgress, Typography } from '@mui/material';
import { EmojiEvents as XPIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import MetricCard from '../common/MetricCard';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { rangeDays, formatCustomLabel, toYmd } from '../common/timeRange';
import { colors } from '../../theme/designTokens';
import './QuestAnalyticsTab.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler
);

const CHART_COLORS = [
  colors.warmGold[500],
  colors.deepTeal[500],
  colors.warmGold[300],
  colors.deepTeal[300],
  colors.slateGray[500],
  colors.warmGold[700],
  colors.deepTeal[700],
  colors.slateGray[300],
  '#146152',
  '#7D1935',
];

const CHART_COLORS_ALPHA = CHART_COLORS.map(c => c + '99');

interface QuestAnalyticsData {
  summary: {
    totalQuests: number;
    uniqueUsers: number;
    avgWordCount: number;
    completedQuests: number;
    draftQuests: number;
    totalXp: number;
    avgXp: number;
    completionRate: number;
    legacyQuestResponses: number;
  };
  trends: Array<{ date: string; count: number; uniqueUsers: number }>;
  writingStyles: Array<{ name: string; count: number }>;
  illustrationStyles: Array<{ name: string; count: number }>;
  moods: Array<{ mood: string; count: number }>;
  topPrompts: Array<{ promptId: number; title: string; count: number }>;
  funnel: { started: number; completed: number; total: number };
  wordCountDistribution: Array<{ bucket: string; count: number }>;
}

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' as const, labels: { padding: 12, usePointStyle: true, font: { size: 11 } } },
  },
};

const QuestAnalyticsTab: React.FC = () => {
  const [data, setData] = useState<QuestAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useTimeRangeParam({ type: '30d' }, { prefix: 'quests' });
  // The endpoint takes a plain day count. "All Time" used to be a 3650-day pill;
  // Custom now covers any span the operator actually wants.
  const timeRange = String(rangeDays(range));
  // ...and a day count counts BACK FROM NOW. A custom window that ends in the
  // past is therefore re-anchored to today by the server, so the pill can read
  // "Jan 1 - Jan 31" over the last 31 days' data. Say so rather than let the
  // pill lie. Same disclosure as RevenueSubscriptionsPage, which shares the
  // `days=` dialect.
  const customEndsInPast = range.type === 'custom' && range.to < toYmd(new Date());

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const result = await apiService.getDetailedQuestAnalytics(timeRange);
      setData(result);
    } catch (error) {
      console.error('Failed to load quest analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const filter = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75, mb: 2.5 }}>
      <div className="quest-analytics-time-picker" style={{ marginBottom: 0 }}>
        <TimeRangeFilter value={range} onChange={setRange} onRefresh={loadAnalytics} refreshing={loading} />
      </div>
      {customEndsInPast && (
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '11.5px',
            color: 'text.secondary',
            maxWidth: 420,
            lineHeight: 1.4,
          }}
        >
          These figures measure back from today. {formatCustomLabel(range)} is
          {' '}{timeRange} days long, so the last {timeRange} days — ending today —
          are what is shown.
        </Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <div className="quest-analytics">
        {filter}
        <div className="quest-analytics-loading">
          <CircularProgress size={32} sx={{ mr: 2 }} />
          Loading analytics...
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="quest-analytics">
        {filter}
        <div className="quest-analytics-loading">No analytics data available</div>
      </div>
    );
  }

  const s = data.summary;
  const funnelMax = Math.max(data.funnel.total, 1);

  return (
    <div className="quest-analytics">
      {/* Time Range Picker */}
      {filter}

      {/* XP Brain Health Banner */}
      <div className="quest-xp-banner">
        <div className="quest-xp-banner-icon">
          <XPIcon sx={{ fontSize: 28, color: '#504405' }} />
        </div>
        <div className="quest-xp-banner-content">
          <div className="quest-xp-banner-label">Total XP Earned Across Platform</div>
          <div className="quest-xp-banner-value">{(s.totalXp || 0).toLocaleString()} XP</div>
          <div className="quest-xp-banner-sub">
            Average {s.avgXp || 0} XP per quest &middot; {s.uniqueUsers} active members
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="quest-analytics-kpis">
        <MetricCard title="Total Quests" value={s.totalQuests} icon="emoji_events" color="content" />
        <MetricCard title="Completed" value={s.completedQuests} icon="check_circle" color="revenue" />
        <MetricCard title="Completion Rate" value={`${s.completionRate}%`} icon="trending_up" color="content" />
        <MetricCard title="Unique Members" value={s.uniqueUsers} icon="people" color="users" />
        <MetricCard title="Avg Word Count" value={s.avgWordCount} icon="description" color="content" />
        <MetricCard title="Legacy Responses" value={s.legacyQuestResponses} icon="history" color="content" />
      </div>

      {/* Charts */}
      <div className="quest-analytics-charts">
        {/* 1. Completion Funnel */}
        <div className="quest-chart-card">
          <h4 className="quest-chart-title">Completion Funnel</h4>
          <div className="quest-funnel">
            <div className="quest-funnel-step">
              <span className="quest-funnel-label">Started</span>
              <div
                className="quest-funnel-bar"
                style={{
                  width: `${Math.max((data.funnel.total / funnelMax) * 100, 20)}%`,
                  background: `linear-gradient(90deg, ${colors.warmGold[500]}, ${colors.warmGold[300]})`,
                }}
              >
                {data.funnel.total}
              </div>
            </div>
            <div className="quest-funnel-step">
              <span className="quest-funnel-label">In Progress</span>
              <div
                className="quest-funnel-bar"
                style={{
                  width: `${Math.max(((data.funnel.started - data.funnel.completed) / funnelMax) * 100, 15)}%`,
                  background: `linear-gradient(90deg, ${colors.deepTeal[400]}, ${colors.deepTeal[300]})`,
                }}
              >
                {data.funnel.started - data.funnel.completed}
              </div>
            </div>
            <div className="quest-funnel-step">
              <span className="quest-funnel-label">Completed</span>
              <div
                className="quest-funnel-bar"
                style={{
                  width: `${Math.max((data.funnel.completed / funnelMax) * 100, 15)}%`,
                  background: `linear-gradient(90deg, #146152, #1A7A68)`,
                }}
              >
                {data.funnel.completed}
              </div>
            </div>
          </div>
        </div>

        {/* 2. Top Quest Prompts (Horizontal Bar) */}
        <div className="quest-chart-card">
          <h4 className="quest-chart-title">Most Popular Quest Prompts</h4>
          <div className="quest-chart-canvas quest-chart-canvas--small">
            {data.topPrompts.length > 0 ? (
              <Bar
                data={{
                  labels: data.topPrompts.map(p => p.title.length > 30 ? p.title.substring(0, 30) + '...' : p.title),
                  datasets: [{
                    label: 'Submissions',
                    data: data.topPrompts.map(p => p.count),
                    backgroundColor: colors.warmGold[500] + '80',
                    borderColor: colors.warmGold[500],
                    borderWidth: 1,
                  }],
                }}
                options={{ ...commonOptions, indexAxis: 'y' as const, scales: { x: { beginAtZero: true } } }}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ADB5BD', fontSize: '0.85rem' }}>
                No prompt data available
              </div>
            )}
          </div>
        </div>

        {/* 3. Quests Created Over Time (Line) — wide */}
        <div className="quest-chart-card quest-chart-card--wide">
          <h4 className="quest-chart-title">Quests Created Over Time</h4>
          <div className="quest-chart-canvas">
            <Line
              data={{
                labels: data.trends.map(t => t.date),
                datasets: [{
                  label: 'Quests',
                  data: data.trends.map(t => t.count),
                  borderColor: colors.warmGold[500],
                  backgroundColor: colors.warmGold[500] + '20',
                  fill: true,
                  tension: 0.3,
                  pointRadius: 3,
                }],
              }}
              options={{
                ...commonOptions,
                scales: {
                  x: { ticks: { maxTicksLimit: 15, font: { size: 10 } } },
                  y: { beginAtZero: true },
                },
              }}
            />
          </div>
        </div>

        {/* 4. Writing Style (Doughnut) */}
        <div className="quest-chart-card">
          <h4 className="quest-chart-title">Writing Style Popularity</h4>
          <div className="quest-chart-canvas quest-chart-canvas--small">
            {data.writingStyles.length > 0 ? (
              <Doughnut
                data={{
                  labels: data.writingStyles.map(s => s.name),
                  datasets: [{ data: data.writingStyles.map(s => s.count), backgroundColor: CHART_COLORS_ALPHA.slice(0, data.writingStyles.length), borderColor: CHART_COLORS.slice(0, data.writingStyles.length), borderWidth: 2 }],
                }}
                options={commonOptions}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ADB5BD', fontSize: '0.85rem' }}>
                No style data available
              </div>
            )}
          </div>
        </div>

        {/* 5. Mood Distribution (Polar Area) */}
        <div className="quest-chart-card">
          <h4 className="quest-chart-title">Mood Distribution</h4>
          <div className="quest-chart-canvas quest-chart-canvas--small">
            {data.moods.length > 0 ? (
              <PolarArea
                data={{
                  labels: data.moods.map(m => m.mood),
                  datasets: [{ data: data.moods.map(m => m.count), backgroundColor: CHART_COLORS_ALPHA.slice(0, data.moods.length) }],
                }}
                options={commonOptions}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ADB5BD', fontSize: '0.85rem' }}>
                No mood data available
              </div>
            )}
          </div>
        </div>

        {/* 6. Word Count Distribution (Bar) */}
        <div className="quest-chart-card">
          <h4 className="quest-chart-title">Word Count Distribution</h4>
          <div className="quest-chart-canvas quest-chart-canvas--small">
            <Bar
              data={{
                labels: data.wordCountDistribution.map(w => w.bucket),
                datasets: [{ label: 'Quests', data: data.wordCountDistribution.map(w => w.count), backgroundColor: colors.deepTeal[400] + '80', borderColor: colors.deepTeal[400], borderWidth: 1 }],
              }}
              options={{ ...commonOptions, scales: { y: { beginAtZero: true } } }}
            />
          </div>
        </div>

        {/* 7. Illustration Style (Doughnut) */}
        <div className="quest-chart-card">
          <h4 className="quest-chart-title">Illustration Style Usage</h4>
          <div className="quest-chart-canvas quest-chart-canvas--small">
            {data.illustrationStyles.length > 0 ? (
              <Doughnut
                data={{
                  labels: data.illustrationStyles.map(s => s.name),
                  datasets: [{ data: data.illustrationStyles.map(s => s.count), backgroundColor: CHART_COLORS_ALPHA.slice(0, data.illustrationStyles.length), borderColor: CHART_COLORS.slice(0, data.illustrationStyles.length), borderWidth: 2 }],
                }}
                options={commonOptions}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ADB5BD', fontSize: '0.85rem' }}>
                No illustration data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestAnalyticsTab;
